/** 
 * @license magicbell v3.3.0
 *
 * Copyright (c) MagicBell Inc. and its affiliates.
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 */

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var _options;
import ky from "ky";
import urlJoin from "url-join";
import { deleteEmptyHeaders, toCurl } from "fetch-addons";
import Debug from "debug";
const hasOwn = (obj, prop) => obj && Object.prototype.hasOwnProperty.call(obj, prop);
function isString(value) {
  return typeof value === "string";
}
function isBoolean(value) {
  return typeof value === "boolean";
}
function isArray(value) {
  return Array.isArray(value);
}
function isStringArray(value) {
  return Array.isArray(value) && value.every((x) => typeof x === "string");
}
function isNumber(value) {
  return typeof value === "number";
}
function isObject(value) {
  return value && typeof value === "object";
}
function joinAnd(...parts) {
  if (parts.length <= 2) return parts.join(" and ");
  const last = parts.slice(-1)[0];
  const others = parts.slice(0, -1);
  return [others.join(", "), last].join(", and ");
}
function joinOr(...parts) {
  if (parts.length <= 2) return parts.join(" or ");
  const last = parts.slice(-1)[0];
  const others = parts.slice(0, -1);
  return [others.join(", "), last].join(", or ");
}
function joinUrlSegments(...segments) {
  return ["/", ...segments].join("/").replace(/\/+/g, "/").replace(/\/$/, "");
}
function tryParse(obj) {
  try {
    return JSON.parse(String(obj));
  } catch (e) {
    return obj;
  }
}
function createError(rawError) {
  for (const field of ["code", "type", "status"]) {
    switch (rawError[field]) {
      case "user_identifier_not_provided":
        return new AuthenticationError(rawError);
      case "invalid_request_error":
        return new InvalidRequestError(rawError);
      case "api_error":
        return new APIError(rawError);
      case "authentication_error":
        return new AuthenticationError(rawError);
      case "rate_limit_error":
        return new RateLimitError(rawError);
      case "idempotency_error":
        return new IdempotencyError(rawError);
      case 404:
        return new NotFoundError(rawError);
      case 422:
        return new UserInputError(rawError);
    }
  }
  return new UnknownError(rawError);
}
class MagicBellError extends Error {
  constructor(raw) {
    super(raw.message);
    /**
     * The name of the error.
     */
    __publicField(this, "name");
    /**
     * The error message returned by the REST API.
     */
    __publicField(this, "message");
    /**
     * The type of the error.
     */
    __publicField(this, "type");
    /**
     * The URL to the documentation for the error.
     */
    __publicField(this, "docsUrl");
    /**
     * The error code returned by the REST API.
     */
    __publicField(this, "code");
    /**
     * The HTTP status code returned by the REST API.
     */
    __publicField(this, "status");
    /**
     * The HTTP status text returned by the REST API.
     */
    __publicField(this, "statusText");
    /**
     * A suggestion on how to fix the error.
     */
    __publicField(this, "suggestion");
    /**
     * The raw response body returned by the REST API.
     */
    __publicField(this, "responseBody");
    this.type = this.constructor.name;
    this.name = "MagicBellError";
    this.code = raw.code;
    this.status = raw.status;
    this.statusText = raw.statusText;
    this.responseBody = raw.responseBody;
    this.message = raw.message;
    this.suggestion = raw.suggestion;
    this.docsUrl = raw.docs_url || raw.help_link;
  }
  /**
   * @deprecated - use docsUrl instead
   */
  get docs_url() {
    return this.docsUrl;
  }
}
class InvalidRequestError extends MagicBellError {
}
class UserInputError extends MagicBellError {
}
class APIError extends MagicBellError {
}
class AuthenticationError extends MagicBellError {
}
class RateLimitError extends MagicBellError {
}
class IdempotencyError extends MagicBellError {
}
class UnknownError extends MagicBellError {
}
class NotFoundError extends MagicBellError {
}
function uuid4() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === "x" ? r : r & 3 | 8;
    return v.toString(16);
  });
}
function getDefaultIdempotencyKey(method, maxRetries) {
  if (method.toUpperCase() !== "POST" || maxRetries === 0) return;
  return "magicbell-retry-".concat(uuid4());
}
function setRequestHeaders(options, request) {
  const idempotencyKey = request.headers.get("idempotency-key") || options.idempotencyKey || getDefaultIdempotencyKey(request.method, options.maxRetries);
  request.headers.set("content-type", "application/json");
  request.headers.set("accept", "application/json");
  request.headers.set("idempotency-key", idempotencyKey);
  if (new URL(request.url).pathname === "/notification_preferences") {
    request.headers.set("accept-version", "v2");
  }
  for (const [key, value] of Object.entries(options.headers || {})) {
    request.headers.set(key, value);
  }
  request.headers.set("user-agent", typeof document !== "undefined" ? "" : getUserAgent(options.appInfo));
  request.headers.set("x-magicbell-client-user-agent", getClientUserAgent(options.appInfo));
  if (options.token) {
    request.headers.set("authorization", "Bearer ".concat(options.token));
    deleteEmptyHeaders(request.headers);
    return;
  }
  request.headers.set("x-magicbell-api-key", options.apiKey);
  if (options.apiSecret) {
    request.headers.set("x-magicbell-api-secret", options.apiSecret);
  } else if (options.userExternalId) {
    request.headers.set("x-magicbell-user-hmac", options.userHmac);
    request.headers.set("x-magicbell-user-external-id", options.userExternalId);
  } else if (options.userEmail) {
    request.headers.set("x-magicbell-user-hmac", options.userHmac);
    request.headers.set("x-magicbell-user-email", options.userEmail);
  }
  deleteEmptyHeaders(request.headers);
}
function withRequestHeaders(options) {
  return {
    beforeRequest: [(request) => setRequestHeaders(options, request)]
  };
}
function getEnvInfo() {
  var _a;
  const common = {
    binding: "magicbell",
    binding_version: "3.3.0",
    publisher: "magicbell"
  };
  if (typeof process === "undefined") {
    return common;
  }
  return __spreadProps(__spreadValues({}, common), {
    runtime: ((_a = process == null ? void 0 : process.release) == null ? void 0 : _a.name) || "node",
    runtime_version: process.version,
    platform: process.platform,
    arch: process.arch
  });
}
function getAppInfoAsString(appInfo) {
  if (!(appInfo == null ? void 0 : appInfo.name)) return "";
  return [appInfo == null ? void 0 : appInfo.name, appInfo.version && "/".concat(appInfo.version), appInfo.url && " (".concat(appInfo.url, ")")].filter(Boolean).join("");
}
function getUserAgent(appInfo) {
  const env = getEnvInfo();
  return [
    "".concat(env.binding, "/").concat(env.binding_version),
    "runtime" in env && "".concat(env.runtime, "/").concat(env.runtime_version),
    getAppInfoAsString(appInfo)
  ].filter(Boolean).join(" ");
}
function getClientUserAgent(appInfo) {
  return JSON.stringify(__spreadProps(__spreadValues({}, getEnvInfo()), { application: appInfo }));
}
Debug("magicbell:info");
const debug = Debug("magicbell:debug");
const error = Debug("magicbell:error");
function mask(str) {
  return str;
}
const secretHeaders = ["x-magicbell-api-secret"];
const excludeHeaders = ["user-agent", "x-magicbell-client-user-agent", "x-magicbell-client-telemetry"];
function withRequestLogging() {
  return {
    beforeRequest: [(request) => debug("".concat(request.method, " ").concat(request.url))],
    beforeError: [
      async (err) => {
        const req = err.request.clone();
        excludeHeaders.forEach((header) => req.headers.delete(header));
        req.headers.forEach((value, key) => {
          if (secretHeaders.includes(key)) req.headers.set(key, mask(value));
        });
        const curl = await toCurl(req).then((x) => x.replace(/\\\n/g, " ").replace(/\s+/g, " "));
        error("".concat(err.message, ": ").concat(curl));
        return err;
      }
    ]
  };
}
const optionValidators = {
  host: isString,
  maxRetryDelay: isNumber,
  timeout: isNumber,
  token: isString,
  apiKey: isString,
  maxRetries: isNumber,
  userEmail: isString,
  userExternalId: isString,
  userHmac: isString,
  idempotencyKey: isString,
  telemetry: isBoolean,
  apiSecret: isString,
  appInfo: isObject,
  features: isObject,
  headers: isObject,
  hooks: isObject
};
function isOptionsHash(object) {
  var _a;
  if (!isObject(object)) return false;
  for (const key of Object.keys(object)) {
    if (!((_a = optionValidators[key]) == null ? void 0 : _a.call(optionValidators, object[key]))) return false;
  }
  return true;
}
function assertHasValidOptions(options) {
  const invalidOptions = Object.keys(options).filter((x) => {
    var _a;
    return options[x] != null && !((_a = optionValidators[x]) == null ? void 0 : _a.call(optionValidators, options[x]));
  });
  if (invalidOptions.length) {
    throw new Error(
      "You have provided invalid client options. Please check the options ".concat(joinAnd(...invalidOptions), ".")
    );
  }
}
function assertHasRequiredOptions(options, required) {
  const missingOptions = required.filter((x) => !hasOwn(options, x) || options[x] == null || options[x] === "");
  if (missingOptions.length) {
    throw new Error("You have not provided all required client options. Please provide ".concat(joinAnd(...missingOptions), "."));
  }
}
function assertHasSomeOptions(options, keys) {
  const missingOptions = keys.filter((x) => !hasOwn(options, x) || options[x] == null || options[x] === "");
  if (missingOptions.length === keys.length) {
    throw new Error("You have not provided any of the required client options. Please provide ".concat(joinOr(...keys), "."));
  }
}
function mergeHooks(...hooks) {
  var _a;
  const result = {};
  for (const hook of hooks) {
    for (const key of Object.keys(hook || {})) {
      (_a = result[key]) != null ? _a : result[key] = [];
      result[key].push(...hook[key]);
    }
  }
  return result;
}
const history = [];
let enabled = true;
function trackRequest(request) {
  request.startTime = Date.now();
  const record = history.pop();
  if (!record || !enabled) return;
  request.headers.set("x-magicbell-client-telemetry", JSON.stringify(record));
}
function captureResponse(request, option, response) {
  if (!(response == null ? void 0 : response.status)) return;
  const { startTime } = request;
  if (!startTime) return;
  history.push({
    id: response.headers.get("x-request-id"),
    runtime: Number(response.headers.get("x-runtime")),
    duration: Date.now() - startTime,
    status: response.status
  });
  return response;
}
function withRequestTelemetry(options) {
  enabled = options.telemetry !== false;
  return {
    beforeRequest: [trackRequest],
    afterResponse: [captureResponse]
  };
}
const DEFAULT_OPTIONS = {
  host: "https://api.magicbell.com",
  timeout: 3e4,
  maxRetries: 3,
  maxRetryDelay: 60,
  telemetry: true
};
class Client {
  constructor(options) {
    __privateAdd(this, _options);
    assertHasValidOptions(options);
    assertHasSomeOptions(options, ["token", "apiKey"]);
    __privateSet(this, _options, __spreadValues(__spreadValues({}, DEFAULT_OPTIONS), options));
  }
  hasFlag(flag) {
    var _a;
    return ((_a = __privateGet(this, _options).features) == null ? void 0 : _a[flag]) || false;
  }
  async request({ method, path, data, params, headers: reqHeaders }, options) {
    const requestOptions = __spreadProps(__spreadValues(__spreadValues({}, __privateGet(this, _options)), options), {
      headers: __spreadValues(__spreadValues({}, __privateGet(this, _options).headers), reqHeaders)
    });
    const url = new URL(urlJoin(requestOptions.host, path));
    for (const [key, value] of Object.entries(params || {})) {
      url.searchParams.append(key, Array.isArray(value) ? value.join(",") : value);
    }
    const hooks = mergeHooks(
      withRequestHeaders(requestOptions),
      withRequestTelemetry(requestOptions),
      withRequestLogging(),
      __privateGet(this, _options).hooks
    );
    return ky(url, {
      method,
      body: data && JSON.stringify(data),
      retry: {
        limit: requestOptions.maxRetries,
        // all methods, as post get idempotency keys
        methods: ["get", "post", "put", "head", "delete", "options", "trace"]
      },
      timeout: requestOptions.timeout,
      hooks
    }).then(
      (response) => response.text().then((text) => JSON.parse(text)).catch(() => void 0)
    ).catch(async (error2) => {
      var _a, _b, _c, _d;
      const body = tryParse(await ((_a = error2 == null ? void 0 : error2.response) == null ? void 0 : _a.text()));
      throw createError(__spreadValues({
        code: error2.code,
        name: error2.name,
        message: error2.message,
        type: error2["type"],
        status: (_b = error2 == null ? void 0 : error2.response) == null ? void 0 : _b.status,
        statusText: (_c = error2 == null ? void 0 : error2.response) == null ? void 0 : _c.statusText,
        responseBody: body
      }, (_d = body == null ? void 0 : body.errors) == null ? void 0 : _d[0]));
    });
  }
}
_options = new WeakMap();
const ASYNC_ITERATOR_SYMBOL = typeof Symbol !== "undefined" && Symbol.asyncIterator ? Symbol.asyncIterator : "@@asyncIterator";
function hasMore(pageResult, nodeCount) {
  if (!hasOwn(pageResult, "current_page") || !hasOwn(pageResult, "per_page")) {
    return false;
  }
  if (hasOwn(pageResult, "total_pages")) {
    return pageResult.current_page < pageResult.total_pages;
  }
  return nodeCount === pageResult.per_page;
}
function autoPaginate(makeRequest, { data, params }) {
  const promiseCache = { currentPromise: null };
  const reverseIteration = typeof params.after !== "undefined";
  let i = 0;
  let request = makeRequest({ data, params });
  const getNextPage = (pageResult) => {
    const page = pageResult.current_page + 1;
    return makeRequest({ data, params: __spreadProps(__spreadValues({}, params), { page }) });
  };
  function iterate(pageResult) {
    const dataKey = Object.keys(pageResult).find((key) => Array.isArray(pageResult[key]));
    const data2 = pageResult[dataKey] || pageResult.data;
    if (!data2 || typeof data2.length !== "number") {
      throw Error("Unexpected: MagicBell API response does not have a well-formed response.");
    }
    if (i < data2.length) {
      const idx = reverseIteration ? data2.length - 1 - i : i;
      const value = data2[idx];
      i += 1;
      return { value, done: false };
    }
    if (hasMore(pageResult, data2.length)) {
      i = 0;
      request = getNextPage(pageResult);
      return request.then(iterate);
    }
    return { value: void 0, done: true };
  }
  function asyncIteratorNext() {
    return memoizedPromise(promiseCache, (resolve, reject) => {
      return request.then(iterate).then(resolve).catch(reject);
    });
  }
  const forEach = makeForEach(asyncIteratorNext);
  const toArray = makeToArray(forEach);
  const autoPaginationMethods = {
    forEach,
    toArray,
    next: asyncIteratorNext,
    return: () => ({}),
    [ASYNC_ITERATOR_SYMBOL]: () => {
      return autoPaginationMethods;
    }
  };
  return Object.assign(request, autoPaginationMethods);
}
function memoizedPromise(promiseCache, cb) {
  if (promiseCache.currentPromise) {
    return promiseCache.currentPromise;
  }
  promiseCache.currentPromise = new Promise(cb).then((ret) => {
    promiseCache.currentPromise = void 0;
    return ret;
  });
  return promiseCache.currentPromise;
}
function makeForEach(asyncIteratorNext, onDoneCallback) {
  return function forEach(onItem) {
    return new Promise((resolve, reject) => {
      let idx = 0;
      function handleIteration(iterResult) {
        if (iterResult == null ? void 0 : iterResult.done) {
          resolve();
          return;
        }
        const item = iterResult == null ? void 0 : iterResult.value;
        return new Promise((resolve2) => {
          resolve2(onItem(item, idx));
        }).then((shouldContinue) => {
          if (shouldContinue === false) {
            onDoneCallback == null ? void 0 : onDoneCallback();
            return handleIteration({ done: true });
          } else {
            idx++;
            return asyncIteratorNext().then(handleIteration);
          }
        });
      }
      asyncIteratorNext().then(handleIteration).catch(reject);
    });
  };
}
function makeToArray(forEach) {
  return function toArray(options) {
    const limit = options == null ? void 0 : options.limit;
    if (!limit) {
      throw Error("You must pass a `limit` option to toArray, e.g., `toArray({ limit: 1000 });`.");
    }
    if (limit > 1e4) {
      throw Error(
        "You cannot specify a limit of more than 10,000 items to fetch in `toArray`; use `forEach` to iterate through longer lists."
      );
    }
    return new Promise((resolve, reject) => {
      const items = [];
      forEach((item) => {
        items.push(item);
        if (items.length >= limit) {
          return false;
        }
      }).then(() => {
        resolve(items);
      }).catch(reject);
    });
  };
}
function createListener(client, args = {}) {
  let eventSource;
  let channels;
  let lastEvent;
  let configPromise;
  const messages = [];
  let resolve;
  let activeCount = 0;
  const pushMessage = (p) => {
    messages.push(p);
    if (resolve) {
      resolve();
      resolve = null;
    }
  };
  async function connect(options) {
    activeCount++;
    if (!channels && !configPromise) {
      configPromise = client.request({ method: "GET", path: "/config" }, options).then((x) => channels = x.ws.channel);
    }
    const auth = await client.request({ method: "POST", path: "/ws/auth" }, options);
    const { token } = await ky("https://rest.ably.io/keys/".concat(auth.keyName, "/requestToken"), {
      method: "POST",
      json: auth
    }).then((x) => x.json());
    await configPromise;
    const sseHost = args.sseHost || "https://realtime.ably.io";
    const url = new URL("sse", sseHost);
    url.searchParams.append("v", "1.1");
    url.searchParams.append("accessToken", token);
    url.searchParams.append("channels", channels);
    url.searchParams.append("heartbeats", "true");
    if (lastEvent) {
      url.searchParams.append("lastEvent", lastEvent);
    }
    if (eventSource) {
      eventSource.close();
    }
    if (activeCount < 1) return;
    messages.length = 0;
    eventSource = new EventSource(url.toString());
    eventSource.addEventListener("message", (event) => {
      if (event.origin && event.origin !== sseHost) return;
      lastEvent = event.lastEventId;
      if (!("data" in event)) return;
      const message = JSON.parse(event.data);
      if (message.type === "close") {
        return pushMessage({ value: null, done: true });
      }
      message.data = message.encoding === "json" ? JSON.parse(message.data) : message.data;
      pushMessage({ value: message, done: false });
    });
    eventSource.addEventListener("close", () => {
      return pushMessage({ value: null, done: true });
    });
    eventSource.addEventListener("error", (msg) => {
      const err = "data" in msg ? JSON.parse(msg.data) : {};
      const isTokenErr = err.code >= 40140 && err.code < 40150;
      if (isTokenErr) {
        eventSource.close();
        connect(options);
      } else if (/invalid channel id/i.test(err.message)) {
        eventSource.close();
        pushMessage({ value: null, done: true });
      } else {
        console.log("sse error:", msg);
      }
    });
  }
  function listen(options) {
    void connect(options);
    const asyncIteratorNext = async () => {
      let event = null;
      while (!event) {
        if (!messages.length) await new Promise((r) => resolve = r);
        event = messages.pop();
      }
      if (!event) return { done: false, value: "" };
      if (event.done && eventSource) eventSource.close();
      return event;
    };
    const dispose = () => {
      activeCount--;
      eventSource == null ? void 0 : eventSource.close();
      pushMessage({ done: true, value: void 0 });
      return { done: true, value: void 0 };
    };
    const forEach = makeForEach(asyncIteratorNext, dispose);
    const autoPaginationMethods = {
      forEach,
      close: () => void dispose(),
      next: asyncIteratorNext,
      return: dispose,
      [ASYNC_ITERATOR_SYMBOL]: () => {
        return autoPaginationMethods;
      }
    };
    return autoPaginationMethods;
  }
  return listen;
}
const queryParamValidators = {
  archived: isBoolean,
  read: isBoolean,
  seen: isBoolean,
  categories: (value) => isString(value) || isStringArray(value),
  topics: (value) => isString(value) || isStringArray(value)
};
function isForcedQueryParams(object) {
  var _a;
  if (!isObject(object)) return false;
  for (const key of Object.keys(object)) {
    if (!((_a = queryParamValidators[key]) == null ? void 0 : _a.call(queryParamValidators, object[key]))) return false;
  }
  return true;
}
function getUrl(path, params, options = { encode: true }) {
  return path.replace(
    /{([\s\S]+?)}/g,
    ($0, $1) => options.encode ? encodeURIComponent(params[$1] || "") : params[$1] || ""
  );
}
function extractUrlParams(path) {
  const params = path.match(/{\w+}/g) || [];
  return (params || []).map((param) => param.replace(/[{}]/g, ""));
}
function getDataFromArgs(args) {
  if (!isArray(args) || !isObject(args[0])) return {};
  if (isOptionsHash(args[0])) return {};
  return args.shift();
}
function getOptionsFromArgs(args) {
  if (!isArray(args) || args.length === 0) return {};
  const arg = args[args.length - 1];
  if (!isOptionsHash(arg)) return {};
  return __spreadValues({}, args.pop());
}
function normalizeArgs({
  path,
  method,
  args
}) {
  const argsCopy = [...args].filter((x) => x !== void 0);
  const urlParams = extractUrlParams(path);
  const urlData = urlParams.reduce((urlData2, param) => {
    const arg = argsCopy.shift();
    if (typeof arg !== "string") {
      throw new Error(
        'MagicBell: Argument "'.concat(param, '" must be a string, but got ').concat(typeof arg, ": ").concat(JSON.stringify(
          arg
        ), " (on API request to `").concat(method, " ").concat(path, "`)")
      );
    }
    urlData2[param] = arg;
    return urlData2;
  }, {});
  const url = getUrl(path, urlData, { encode: false });
  const dataFromArgs = getDataFromArgs(argsCopy);
  const options = getOptionsFromArgs(argsCopy);
  if (argsCopy.filter((x) => x != null).length) {
    throw new Error(
      "MagicBell: Unknown arguments (".concat(JSON.stringify(argsCopy), "). (on API request to `").concat(method, " ").concat(url, "`)")
    );
  }
  let dataInQuery = method === "GET";
  if (method === "POST" && isForcedQueryParams(dataFromArgs)) {
    dataInQuery = true;
  }
  const data = dataInQuery ? {} : dataFromArgs;
  const params = dataInQuery ? dataFromArgs : {};
  return { path: url, data, params, options };
}
function isEmptyPayload(data) {
  if (!data) return true;
  if (Array.isArray(data)) return data.length === 0;
  if (typeof data === "object") return Object.keys(data).length === 0;
  return false;
}
class Resource {
  constructor(client) {
    __publicField(this, "path");
    __publicField(this, "entity");
    __publicField(this, "client");
    this.client = client;
  }
  request({ method, paged, path: tplPath }, ...args) {
    const { path, data, params, options } = normalizeArgs({
      path: joinUrlSegments(this.path, tplPath),
      method,
      args
    });
    const makeRequest = ({ data: data2, params: params2 }) => {
      const entity = this.entity || this.path;
      data2 = isEmptyPayload(data2) ? void 0 : { [entity]: data2 };
      params2 = isEmptyPayload(params2) ? void 0 : params2;
      return this.client.request({ method, path, data: data2, params: params2 }, options).then((response) => (response == null ? void 0 : response[entity]) || response);
    };
    if (paged) {
      return autoPaginate(makeRequest, {
        data,
        params
      });
    }
    return makeRequest({ data, params });
  }
  assertFeatureFlag(flag) {
    if (!this.client.hasFlag(flag)) {
      throw new Error('This is a beta feature, please enable it by providing the "'.concat(flag, '" feature flag.'));
    }
  }
}
class NotificationPreferences extends Resource {
  constructor() {
    super(...arguments);
    __publicField(this, "path", "notification_preferences");
    __publicField(this, "entity", "notification_preferences");
  }
  /**
   * Fetch a user's notification preferences. If a user does not disable a channel
   * explicitly, we would send notifications through that channel as long as your
   * project is enabled.
   *
   * @param options - override client request options.
   * @returns
   **/
  get(options) {
    return this.request(
      {
        method: "GET"
      },
      options
    );
  }
  update(dataOrOptions, options) {
    return this.request(
      {
        method: "PUT"
      },
      dataOrOptions,
      options
    );
  }
}
class Notifications extends Resource {
  constructor() {
    super(...arguments);
    __publicField(this, "path", "notifications");
    __publicField(this, "entity", "notification");
  }
  /**
   * Mark a user notification as archived.
   *
   * @param notificationId - ID of the user notification.
   *   The ID of a user notification can be obtained from the "Fetch user
   *   notifications" API endpoint or from push events sent to the MagicBell React
   *   library.
   *
   * @param options - override client request options.
   **/
  archive(notificationId, options) {
    return this.request(
      {
        method: "POST",
        path: "{notification_id}/archive"
      },
      notificationId,
      options
    );
  }
  markAllRead(dataOrOptions, options) {
    return this.request(
      {
        method: "POST",
        path: "read"
      },
      dataOrOptions,
      options
    );
  }
  markAllSeen(dataOrOptions, options) {
    return this.request(
      {
        method: "POST",
        path: "seen"
      },
      dataOrOptions,
      options
    );
  }
  /**
   * Mark a user notification as read. The notification will be automatically marked
   * as seen, too.
   *
   * The new state will be reflected in the user's notification inbox in real-time.
   *
   * @param notificationId - ID of the user notification.
   *   The ID of a user notification can be obtained from the "Fetch user
   *   notifications" API endpoint or from push events sent to the MagicBell React
   *   library.
   *
   * @param options - override client request options.
   **/
  markAsRead(notificationId, options) {
    return this.request(
      {
        method: "POST",
        path: "{notification_id}/read"
      },
      notificationId,
      options
    );
  }
  /**
   * Mark a user notification as unread. The new state will be reflected in the
   * user's notification inbox in real-time.
   *
   * @param notificationId - ID of the user notification.
   *   The ID of a user notification can be obtained from the "Fetch user
   *   notifications" API endpoint or from push events sent to the MagicBell React
   *   library.
   *
   * @param options - override client request options.
   **/
  markAsUnread(notificationId, options) {
    return this.request(
      {
        method: "POST",
        path: "{notification_id}/unread"
      },
      notificationId,
      options
    );
  }
  list(dataOrOptions, options) {
    return this.request(
      {
        method: "GET",
        paged: true
      },
      dataOrOptions,
      options
    );
  }
  /**
   * Fetch a user's notification by its ID.
   *
   * @param notificationId - ID of the user notification.
   *   The ID of a user notification can be obtained from the "Fetch user
   *   notifications" API endpoint or from push events sent to the MagicBell React
   *   library.
   *
   * @param options - override client request options.
   * @returns
   **/
  get(notificationId, options) {
    return this.request(
      {
        method: "GET",
        path: "{notification_id}"
      },
      notificationId,
      options
    );
  }
  /**
   * Delete a user's notification by its ID. The notification is deleted immediately
   * and removed from the user's notification inbox in real-time.
   *
   * @param notificationId - ID of the user notification.
   *   The ID of a user notification can be obtained from the "Fetch user
   *   notifications" API endpoint or from push events sent to the MagicBell React
   *   library.
   *
   * @param options - override client request options.
   **/
  delete(notificationId, options) {
    return this.request(
      {
        method: "DELETE",
        path: "{notification_id}"
      },
      notificationId,
      options
    );
  }
  /**
   * Mark a user notification as unarchived.
   *
   * @param notificationId - ID of the user notification.
   *   The ID of a user notification can be obtained from the "Fetch user
   *   notifications" API endpoint or from push events sent to the MagicBell React
   *   library.
   *
   * @param options - override client request options.
   **/
  unarchive(notificationId, options) {
    return this.request(
      {
        method: "DELETE",
        path: "{notification_id}/archive"
      },
      notificationId,
      options
    );
  }
}
class PushSubscriptions extends Resource {
  constructor() {
    super(...arguments);
    __publicField(this, "path", "push_subscriptions");
    __publicField(this, "entity", "push_subscription");
  }
  /**
   * Register a device token for push notifications.
   *
   * Please keep in mind that mobile push notifications will be delivered to this
   * device only if the channel is configured and enabled.
   *
   * @param data
   * @param options - override client request options.
   * @returns
   **/
  create(data, options) {
    return this.request(
      {
        method: "POST"
      },
      data,
      options
    );
  }
  /**
   * Returns the list of device tokens registered for push notifications.
   *
   * @param options - override client request options.
   * @returns
   **/
  list(options) {
    return this.request(
      {
        method: "GET",
        paged: true
      },
      options
    );
  }
  /**
   * Deletes the registered device token to remove the mobile push subscription.
   *
   * @param deviceToken - Token of the device you want to remove
   * @param options - override client request options.
   **/
  delete(deviceToken, options) {
    return this.request(
      {
        method: "DELETE",
        path: "{device_token}"
      },
      deviceToken,
      options
    );
  }
}
class Subscriptions extends Resource {
  constructor() {
    super(...arguments);
    __publicField(this, "path", "subscriptions");
    __publicField(this, "entity", "subscription");
  }
  /**
   * Set a user's subscription status to subscribed for a particular topic (and
   * optional categories). If the user previously unsubscribed, the user will be
   * resubscribed.
   *
   * @param data
   * @param options - override client request options.
   * @returns
   **/
  create(data, options) {
    return this.request(
      {
        method: "POST"
      },
      data,
      options
    );
  }
  /**
   * Unusbscribe a user from a particular topic (and optional categories).
   *
   * @param topic - The topic for which we'd like to filter topic subscriptions.
   * @param data
   * @param options - override client request options.
   * @returns
   **/
  unsubscribe(topic, data, options) {
    return this.request(
      {
        method: "POST",
        path: "{topic}/unsubscribe"
      },
      topic,
      data,
      options
    );
  }
  /**
   * Fetch a user's topic subscriptions.
   *
   * @param options - override client request options.
   * @returns
   **/
  list(options) {
    return this.request(
      {
        method: "GET",
        paged: true
      },
      options
    );
  }
  /**
   * Show a user's subscription status for a particular topic and categories.
   *
   * @param topic - The topic for which we'd like to filter topic subscriptions.
   * @param options - override client request options.
   * @returns
   **/
  get(topic, options) {
    return this.request(
      {
        method: "GET",
        path: "{topic}"
      },
      topic,
      options
    );
  }
  delete(topic, dataOrOptions, options) {
    return this.request(
      {
        method: "DELETE",
        path: "{topic}"
      },
      topic,
      dataOrOptions,
      options
    );
  }
}
class UserClient extends Client {
  constructor(options) {
    assertHasRequiredOptions(options, ["apiKey"]);
    if ("apiSecret" in options) {
      throw new Error("The API secret should NOT be used on the user client.");
    }
    super(options);
    __publicField(this, "listen", createListener(this));
    __publicField(this, "notificationPreferences", new NotificationPreferences(this));
    __publicField(this, "notifications", new Notifications(this));
    __publicField(this, "pushSubscriptions", new PushSubscriptions(this));
    __publicField(this, "subscriptions", new Subscriptions(this));
  }
}
export {
  UserClient
};
//# sourceMappingURL=user-client.mjs.map
